import { extractPhoneNumberDigits } from "@src/appV2/lib";
import { checkIfPhoneExists } from "@src/appV2/User/api/checkUserExists";
import { z } from "zod";

/**
 * Normalize phone number to 10-digit format by stripping leading "1" country code if present
 */
export function normalizeTo10Digits(digits: string): string {
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
}

/**
 * Creates a schema for validating phone number input (10 digits)
 * @param existingPhone - The user's current phone number. If provided, the existence check
 *                        will be skipped when the phone matches, avoiding false "already in use" errors.
 */
export function createPhoneFormSchema(existingPhone?: string) {
  const normalizedExistingPhone = existingPhone
    ? normalizeTo10Digits(extractPhoneNumberDigits(existingPhone))
    : undefined;

  return z.object({
    phone: z
      .string()
      .refine(
        (value) => {
          // Remove all non-digit characters
          const digits = value.replace(/\D/g, "");
          return digits.length === 10;
        },
        {
          message: "Please enter a valid 10-digit phone number.",
        }
      )
      .refine(
        async (value) => {
          try {
            const phoneDigits = extractPhoneNumberDigits(value);
            const normalizedNewPhone = normalizeTo10Digits(phoneDigits);

            // Skip existence check if the phone matches the user's current phone
            if (normalizedExistingPhone && normalizedNewPhone === normalizedExistingPhone) {
              return true;
            }

            const { exists } = await checkIfPhoneExists(phoneDigits);
            return !exists;
          } catch {
            // If the API call fails, allow validation to pass
            // The error will be handled at a higher level if needed
            return true;
          }
        },
        {
          message:
            "The phone number you tried to submit is already in use by another account. Please make sure you are submitting a phone number you own and that you typed it correctly.",
        }
      ),
  });
}

/**
 * Default schema for validating phone number input (10 digits)
 * Used for phone number update when there's no existing phone to compare against
 */
export const phoneFormSchema = createPhoneFormSchema();
